import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import QuoteRow from './QuoteRow';
import { StatusFilters } from './StatusUI';

const API = process.env.REACT_APP_BACKEND_URL;

const QuotesTab = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/quote-requests`);
      if (r.ok) setQuotes(await r.json());
    } catch (_) { /* network error */ }
    setLoading(false);
  };

  useEffect(() => { fetchQuotes(); }, []);

  const changeStatus = async (id, status) => {
    // optimistic update
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
    try {
      await fetch(`${API}/api/quote-requests/${id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
    } catch (_) { /* silent */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette demande de devis ?')) return;
    await fetch(`${API}/api/quote-requests/${id}`, { method: 'DELETE' });
    fetchQuotes();
  };

  const counts = useMemo(() => ({
    all: quotes.length,
    nouveau: quotes.filter((q) => (q.status || 'nouveau') === 'nouveau').length,
    lu: quotes.filter((q) => q.status === 'lu').length,
    traite: quotes.filter((q) => q.status === 'traite').length,
  }), [quotes]);

  const filtered = useMemo(() => (
    filter === 'all' ? quotes : quotes.filter((q) => (q.status || 'nouveau') === filter)
  ), [quotes, filter]);

  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Demandes de devis</h2>
          <p className="text-sm text-gray-500 mt-1">
            {counts.all} demande{counts.all > 1 ? 's' : ''} • {counts.nouveau} nouveau{counts.nouveau > 1 ? 'x' : ''}
          </p>
        </div>
        <button
          onClick={fetchQuotes}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          data-testid="quotes-refresh-btn"
        >
          <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      <StatusFilters counts={counts} active={filter} onChange={setFilter} />

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center text-gray-500 shadow-sm" data-testid="quotes-empty">
          {loading ? 'Chargement...' : 'Aucune demande dans cette catégorie.'}
        </div>
      ) : (
        <div className="space-y-3" data-testid="quotes-list">
          {filtered.map((q) => (
            <QuoteRow
              key={q.id}
              q={q}
              expanded={expanded === q.id}
              onToggle={() => setExpanded(expanded === q.id ? null : q.id)}
              onDelete={() => handleDelete(q.id)}
              onMarkRead={(id) => changeStatus(id, 'lu')}
              onMarkProcessed={(id) => changeStatus(id, 'traite')}
              onMarkNew={(id) => changeStatus(id, 'nouveau')}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default QuotesTab;
